import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { catchError, exhaustMap, map, Observable, of, switchMap, timer } from 'rxjs';
import { Alert, AlertResponse } from '../models/alert.model';
import { ConfigurationManagerService } from './configuration-manager.service';
import { IncidentCategoryInfoResponse, OrganizationIncidentCategoryResponse } from '../models/incident-category.model';
import * as Cesium from 'cesium';
import { AlertPinMapEntity } from './alert-pin-map-entity.service';
import { GenericAlert } from './generic-alert-factory.service';
import { CameraService } from './camera.service';
import { CoreService } from './core.service';
import { DataService } from './data.service';
import _ from 'lodash';
import { OrganizationUtilService } from './organization-util.service';
import moment from 'moment';
import { DataFeedNotificationService } from './data-feed-notification.service';
import { GlobeService } from './globe.service';
import { TimelineService } from './timeline.service';
import { WindowService } from './window.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataContextService } from './data-context.service';

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private alertList: any[] = [];
  private alertIconDataSource?: Cesium.CustomDataSource;
  private alertIconDataSourceAdded = false;
  private _organizationIncidentCategoryInfo: any = null;
  
  private lastAlertPollTime: string | undefined = undefined;
  private pendingAlerts: { alert: any, isDismissed: boolean }[] = [];
  
  constructor(private api : ApiService,
    private cameraService : CameraService,
    private coreService: CoreService,
    private dataService: DataService,
    private orgUtil: OrganizationUtilService,
    private dataFeedNotificationService: DataFeedNotificationService,
    private globeService: GlobeService,
    private timelineService: TimelineService,
    private windowService: WindowService,
    private dataContextService: DataContextService,
    private modalService: NgbModal
   ){}

  private ensureDataSource(): boolean {
    const viewer = this.globeService.getViewer();
    if (!viewer || !viewer.dataSources) {
      return false;
    }
    if (!this.alertIconDataSource || !viewer.dataSources.contains(this.alertIconDataSource)) {
      this.alertIconDataSource = new Cesium.CustomDataSource('alert-icons');
      this.setupClustering();
      viewer.dataSources.add(this.alertIconDataSource);
      this.alertIconDataSourceAdded = true;
    }
    return true;
  }

  private setupClustering() {
    if (!this.alertIconDataSource) return;
    this.alertIconDataSource.clustering.clusterEvent.addEventListener((clusteredEntities, cluster) => {
      cluster.label.show = false;
      cluster.billboard.show = true;
      cluster.billboard.verticalOrigin = Cesium.VerticalOrigin.BOTTOM;
      cluster.billboard.disableDepthTestDistance = Number.POSITIVE_INFINITY;
      cluster.billboard.id = { type: 'alertCluster',  members: cluster.label.id };
      let pinBuilder = new Cesium.PinBuilder();
      let pin = pinBuilder.fromText(clusteredEntities.length.toString(), Cesium.Color.BLUE, 72).toDataURL();
      cluster.billboard.image = pin;
    });
    this.alertIconDataSource.clustering.pixelRange = 20;
    this.alertIconDataSource.clustering.minimumClusterSize = 2;
    this.alertIconDataSource.clustering.enabled = true;
  }
      
  createAlert(alert: any, isDismissed = false) {
    if (!this.ensureDataSource()) {
      this.pendingAlerts.push({ alert, isDismissed });
      return;
    }

    const newAlert = { ...alert };
    newAlert.facility = '';
    newAlert.facilityId = '';
    const date = new Date(alert.createdAt);
    newAlert.date = date.toISOString().replace('T', ' ').replace(/\.\d\d\dZ/, ' UTC');
    newAlert.occurredAt = alert.occuredAt?.replace('T', ' ').replace(/\.\d\d\dZ/, ' UTC');
    const alertMapEntity = new AlertPinMapEntity(0, 0, newAlert, this.cameraService ,this.coreService,this.windowService,this.dataContextService,this.modalService,this.timelineService);
    alertMapEntity.icon = 'assets/img/alert/DATAWATCH.png';
    const alertObj = new GenericAlert(
      this.cameraService,
      this.orgUtil,
      null,
      null,
      this.dataService,
      this.coreService,
      newAlert,
      alertMapEntity
    );
    alertObj.setEntity(alertMapEntity);
    alertObj.entity = alertMapEntity;

    if (isDismissed) {
      this.dataFeedNotificationService.addDismissedNotificationObject(alertObj);
    } else {
      this.dataFeedNotificationService.addNotificationObject(alertObj);
      this.timelineService.addItemEventToTimeline(alertMapEntity, '', alertObj.id, 0, alertObj.timelineAlertId);
      if (alertObj.status === 'active') {
        const position = alertObj.getAlertPosition();
        if (position && this.alertIconDataSource) {
          const entity = this.alertIconDataSource.entities.add({
            position,
            billboard: {
              image: 'assets/img/arcangel/alert-icon.png',
              scale: 0.75,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM
            },
            name: alertObj.entity.object.name,
            properties: {
              type: 'Alert Marker',
              alertEntity: alertObj,
              arcLiveMapEntity: alertObj
            }
          });
           (entity as any).type = 'Alert Marker';
          (entity as any).alertEntity = alertObj;
          (entity as any).arcLiveMapEntity = alertObj;
        } else {
          // console.error('Alert skipped (no position or datasource):', alertObj);
        }
      }
    }
  }

  getOrganizationIncidentCategoryInfo(): Observable<any> {
    if (this._organizationIncidentCategoryInfo) {
      return of(this._organizationIncidentCategoryInfo);
    }
    return this.dataService.getOrganizationIncidentCategoryInfo().pipe(
      map(info => {
        this._organizationIncidentCategoryInfo = info;
        return info;
      })
    );
  }

  isInAlertList(alert: any): boolean {
    return this.alertList.some((a) => a.id === alert.id);
  }

  isSameAlert(alert: any, noti: any): boolean {
    return alert.id && noti.entity?.object && alert.id === noti.entity.object.id;
  }

  updateAlertStatus(alert: any, noti: any): void {
    if (alert.dismissedAt && alert.dismissedAt.toLowerCase() !== 'infinity') {
      noti.status = 'dismissed';
    } else if (this.isExpired(alert)) {
      noti.status = 'expired';
    } else {
      noti.status = 'active';
    }
    if (alert.viewedAt && alert.viewedAt.toLowerCase() !== 'infinity') {
      noti.alertRead = true;
    } else {
      noti.alertRead = null;
    }
    this.dataFeedNotificationService.addUpdate();
  }
  isExpired(attr: any): boolean {
    if (
      attr.expiresAt &&
      attr.expiresAt.toLowerCase() !== 'infinity' &&
      moment(attr.expiresAt).unix() < moment().unix()
    ) {
      return true;
    }
    return false;
  }

  parseAlertResponse(alerts: any[]): void {
    const notifications = this.dataFeedNotificationService.getNotificationsAsList();
    const finalAlerts: any[] = [];
    alerts = _.reverse(alerts);
    alerts.forEach((alert) => {
      if (!this.isInAlertList(alert)) {
        this.alertList.push(alert);
        finalAlerts.push(alert);
      }
    });

    finalAlerts.forEach((alert) => {
      this.createAlert(alert);
    });

    // Update alerts
    notifications.forEach((noti: any) => {
      alerts.forEach((alert: any) => {
        if (this.isSameAlert(alert, noti)) {
          this.updateAlertStatus(alert, noti);
        }
      });
    });
  }

  getBuildingInfo(alert: any): any[] {
    const buildingInfo: any[] = [];
    const myPerimeters = this.orgUtil.data.myPerimeters;
    const perimeterIds = _.uniq(_.concat(alert.sensorPerimeterIds, alert.perimeterIds ? alert.perimeterIds : []));
    perimeterIds.forEach((id: string) => {
      if (!myPerimeters[id]) {
        return;
      }
      let floor: any;
      let building: any;
      if (myPerimeters[id].perimeterType === 'ROOM') {
        const floors = _.filter(myPerimeters, (o) =>
          _.includes(myPerimeters[id].ancestors, o.arcAngelId) && o.perimeterType === 'FLOOR'
        );
        floor = floors.length > 0 ? floors[0] : null;

        if (floor) {
          const buildings = _.filter(myPerimeters, (o) =>
            _.includes(myPerimeters[floor.arcAngelId].ancestors, o.arcAngelId) && o.perimeterType === 'BUILDING'
          );
          building = buildings.length > 0 ? buildings[0] : null;
        }
        buildingInfo.push({ building, floor, room: myPerimeters[id] });
      } else if (myPerimeters[id].perimeterType === 'FLOOR') {
        const buildings = _.filter(myPerimeters, (o) =>
          _.includes(myPerimeters[id].ancestors, o.arcAngelId) && o.perimeterType === 'BUILDING'
        );
        building = buildings.length > 0 ? buildings[0] : null;
        buildingInfo.push({ building, floor: myPerimeters[id], room: null });
      } else if (myPerimeters[id].perimeterType === 'BUILDING') {
        buildingInfo.push({ building: myPerimeters[id], floor: null, room: null });
      }
    });

    return buildingInfo;
  }

startAlertPoller() {
  timer(0, 5000).pipe(
    exhaustMap(() => {
      if (!this.dataService.getSensorList) {
        return this.dataService.getSensorList().pipe(
          catchError(error => of(null))
        );
      }
      return of(this.dataService.getSensorList);
    }),
    exhaustMap(() => {
      if (!this.dataService.getSensorList) {
        return of(null);
      }
      return this.getOrganizationIncidentCategoryInfo().pipe(
        switchMap((organizationIncidentCategoryInfo) => {
          if (!this._organizationIncidentCategoryInfo) {
            this._organizationIncidentCategoryInfo = organizationIncidentCategoryInfo;
          }
          return this.dataService.findActiveAlert(this.lastAlertPollTime);
        })
      );
    }),
    catchError(error => of(null))
  ).subscribe((alerts: any) => {
    if (alerts && alerts.length > 0) {
      this.lastAlertPollTime = alerts
        .map((o: any) => o.updatedAt)
        .sort()
        .reverse()[0];

      alerts.forEach((alert: any) => {
        if (this._organizationIncidentCategoryInfo[alert.organizationIncidentCategoryId]) {
          alert.incidentCategory = this._organizationIncidentCategoryInfo[alert.organizationIncidentCategoryId].incidentCategory;
        }
      });

      alerts.forEach((alert: any) => {
        alert.buildingInfo = this.getBuildingInfo(alert);
      });

      this.parseAlertResponse(alerts);
    }
  });
}
  getAlertNotifications(): Observable<AlertResponse> {
    const url = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.AlertNotificationCount;
    const now = new Date().toISOString();
    const body = {
      where: {
        expiresAt: { $gt: now },
        dismissedAt: { $gt: now }
      },
      order: [["createdAt", "DESC"]]
    };
    return this.api.post<AlertResponse>(url, body);
  }

getIncidentCategoryById(id: string): Observable<IncidentCategoryInfoResponse> {
  const url = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.IncidentCategoryInfo;
  const body = {
    where: { id: [id] },
    limit: 10000
  };
  return this.api.post<IncidentCategoryInfoResponse>(url, body);
}
getAlertById(id: string): Observable<any> {
  const url = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.AlertPayload;
  const body = {
    where: { alertId: id }
  };
  return this.api.post<any>(url, body);
}
removeAlertIconFromMap(alertId: string) {
  if (!this.alertIconDataSource || !this.alertIconDataSource.entities) return;
  const alerts = this.alertIconDataSource.entities.values.filter((o: any) => o.alertEntity?.alertId === alertId);
  if (alerts.length > 0) {
    this.alertIconDataSource.entities.remove(alerts[0]);
    const clustering = this.alertIconDataSource.clustering;
    if (clustering) {
      clustering.enabled = false;
      clustering.enabled = true;
    }
  }
}

findDismissedAlert(page: number, keyword: string, oics: any): Observable<any> {
  return this.getOrganizationIncidentCategoryInfo().pipe(
    switchMap((organizationIncidentCategoryInfo) => {
      if (!this._organizationIncidentCategoryInfo) {
        this._organizationIncidentCategoryInfo = organizationIncidentCategoryInfo;
      }
      return this.dataService.findDismissedAlert(page, keyword, oics).pipe(
        map((alerts: any) => {
          // Enrich alerts
          alerts.data.forEach((alert: any) => {
            if (this._organizationIncidentCategoryInfo[alert.organizationIncidentCategoryId]) {
              alert.incidentCategory = this._organizationIncidentCategoryInfo[alert.organizationIncidentCategoryId].incidentCategory;
            }
            alert.buildingInfo = this.getBuildingInfo(alert);
          });
          // Update notification service
          this.parseDismissedAlertResponse(alerts.data);
          this.dataFeedNotificationService.addUpdate();
          return alerts;
        })
      );
    })
  );
}
  parseDismissedAlertResponse(alerts: any[]) {
    alerts = _.reverse(alerts);
    this.dataFeedNotificationService.resetDismissedNotifications();
    this.dataFeedNotificationService.addUpdate();
    alerts.forEach((alert: any) => {
      this.createAlert(alert, true);
    });
  }
  startAlertRunner(): void {
    const savedviewsDir = document.getElementById('savedviews-dir');
    if (savedviewsDir) {
      savedviewsDir.style.marginBottom = '72px';
    }
    this.autoExpireAlerts();
    this.startAlertPoller();
  }

  private autoExpireAlerts(): void {
    setTimeout(() => {
      this.autoExpireAlerts();
    }, 4000);

    const alerts = _.filter(this.dataFeedNotificationService.getNotificationsAsList(), (o: any) => {
      return o.status === 'active';
    });
    _.forEach(alerts, (alert: any) => {
      const expiresAt = alert?.entity?.object?.expiresAt;
      if (this.isExpired({ expiresAt })) {
        alert.status = 'expired';
        this.removeAlertIconFromMap(alert.alertId);
        if (typeof alert.removeGeometry === 'function') {
          alert.removeGeometry();
        }
        if (alert.id) {
          this.dataFeedNotificationService.removeNotification(alert.id);
        }
      }
    });
  }

  public processPendingAlerts() {
    if (!this.ensureDataSource()) return;
    while (this.pendingAlerts.length > 0) {
      const { alert, isDismissed } = this.pendingAlerts.shift()!;
      this.createAlert(alert, isDismissed);
    }
  }
}